import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Pencil, Plus, Trash2, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Th, Td } from "@/pages/CustomersPage";
import { useAuth } from "@/contexts/AuthContext";
import SelectWithAdd from "@/components/SelectWithAdd";
import StatusBadge from "@/components/StatusBadge";
import PhotoPicker from "@/components/PhotoPicker";

const EMPTY = { username: "", password: "", name: "", role: "user", status: "Active", photo: "" };

export default function UsersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  // create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);

  // edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "user", status: "Active", new_password: "", photo: "" });
  const [editing_busy, setEditingBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setItems(r.data));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!createForm.username || !createForm.password) {
      toast.error("Username and password are required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/auth/register", createForm);
      toast.success("User created");
      setCreateOpen(false);
      setCreateForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({ name: row.name || "", role: row.role || "user", status: row.status || "Active", new_password: "", photo: row.photo || "" });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditingBusy(true);
    try {
      const payload = { name: editForm.name, role: editForm.role, status: editForm.status, photo: editForm.photo };
      if (editForm.new_password) payload.new_password = editForm.new_password;
      await api.put(`/users/${editing.id}`, payload);
      toast.success(
        editForm.new_password
          ? "User updated & password reset"
          : "User updated"
      );
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setEditingBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete user "${row.username}"?`)) return;
    try {
      await api.delete(`/users/${row.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        overline="Administration"
        title="Users"
        description="Create, edit, or delete login accounts. Reset a user's password by entering a new one in the edit dialog."
        actions={
          <Button
            data-testid="user-add-button"
            onClick={() => { setCreateForm(EMPTY); setCreateOpen(true); }}
            className="rounded-none vsm-primary-bg hover:opacity-90"
          >
            <Plus className="w-4 h-4 mr-2" /> Add User
          </Button>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-5 sm:py-8">
        <div className="border vsm-border vsm-surface overflow-x-auto vsm-table-scroll" data-testid="users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b vsm-border text-left">
                <Th>Username</Th>
                <Th>Name</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Password Changed</Th>
                <Th className="w-32 text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 vsm-muted text-center">No users yet.</td>
                </tr>
              )}
              {items.map((r) => (
                <tr key={r.id} className="border-b vsm-border hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                  <Td className="font-semibold flex items-center gap-2">
                    {r.username}
                    {r.username === user?.username && (
                      <span className="vsm-overline text-[10px]">(you)</span>
                    )}
                  </Td>
                  <Td>{r.name || "—"}</Td>
                  <Td>
                    <span className={`vsm-overline inline-flex items-center gap-1 ${r.role === "admin" ? "vsm-primary-text" : ""}`}>
                      {r.role === "admin" && <ShieldCheck className="w-3 h-3" />}
                      {r.role}
                    </span>
                  </Td>
                  <Td><StatusBadge status={r.status} /></Td>
                  <Td className="font-mono text-xs vsm-muted">
                    {r.created_at ? String(r.created_at).slice(0, 10) : "—"}
                  </Td>
                  <Td className="font-mono text-xs vsm-muted">
                    {r.password_changed_at ? String(r.password_changed_at).slice(0, 10) : "—"}
                  </Td>
                  <Td className="text-right">
                    <button
                      onClick={() => openEdit(r)}
                      data-testid={`user-edit-${r.id}`}
                      className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 mr-1"
                      title="Edit user / reset password"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => remove(r)}
                      data-testid={`user-delete-${r.id}`}
                      disabled={r.username === user?.username}
                      className="p-1.5 hover:bg-red-100 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={r.username === user?.username ? "Can't delete your own account" : "Delete"}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-none border vsm-border max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <div className="vsm-overline">Create</div>
            <DialogTitle className="text-2xl font-bold tracking-tight">New User</DialogTitle>
            <DialogDescription className="vsm-muted text-sm">
              Usernames are case-insensitive; passwords are case-sensitive (min 6 characters).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4 mt-2">
            <div>
              <Label className="vsm-overline">Username <span className="text-red-600">*</span></Label>
              <Input data-testid="user-username" className="rounded-none mt-2" value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} required />
            </div>
            <div>
              <Label className="vsm-overline">Full Name</Label>
              <Input data-testid="user-name" className="rounded-none mt-2" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
            </div>
            <div>
              <Label className="vsm-overline">Password <span className="text-red-600">*</span></Label>
              <Input data-testid="user-password" type="password" className="rounded-none mt-2" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} required minLength={6} />
            </div>
            <div>
              <Label className="vsm-overline">Role</Label>
              <div className="mt-2">
                <SelectWithAdd
                  testId="user-role"
                  value={createForm.role}
                  onChange={(v) => setCreateForm({ ...createForm, role: v })}
                  options={[{ id: "user", label: "User" }, { id: "admin", label: "Admin" }]}
                />
              </div>
            </div>
            <div>
              <Label className="vsm-overline">Profile Photo</Label>
              <div className="mt-2">
                <PhotoPicker
                  testIdPrefix="user-photo"
                  value={createForm.photo}
                  onChange={(v) => setCreateForm({ ...createForm, photo: v })}
                />
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setCreateOpen(false)} data-testid="user-cancel">Cancel</Button>
              <Button type="submit" disabled={creating} className="rounded-none vsm-primary-bg hover:opacity-90" data-testid="user-save">
                {creating ? "Creating…" : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-none border vsm-border max-w-md" data-testid="user-edit-dialog">
          <DialogHeader>
            <div className="vsm-overline">Edit</div>
            <DialogTitle className="text-2xl font-bold tracking-tight">
              {editing?.username}
            </DialogTitle>
            <DialogDescription className="vsm-muted text-sm">
              Update name and role. Optionally enter a new password to reset it (min 6 chars). Username cannot be changed.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-2">
            <div>
              <Label className="vsm-overline">Username</Label>
              <Input
                disabled
                className="rounded-none mt-2 bg-zinc-100 dark:bg-zinc-800"
                value={editing?.username || ""}
                data-testid="user-edit-username"
              />
            </div>
            <div>
              <Label className="vsm-overline">Full Name</Label>
              <Input
                data-testid="user-edit-name"
                className="rounded-none mt-2"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label className="vsm-overline">Role</Label>
              <div className="mt-2">
                <SelectWithAdd
                  testId="user-edit-role"
                  value={editForm.role}
                  onChange={(v) => setEditForm({ ...editForm, role: v })}
                  options={[{ id: "user", label: "User" }, { id: "admin", label: "Admin" }]}
                  disabled={editing?.username === user?.username}
                />
              </div>
              {editing?.username === user?.username && (
                <p className="text-xs vsm-muted mt-1">You can&apos;t change your own role.</p>
              )}
            </div>
            <div>
              <Label className="vsm-overline">Status</Label>
              <div className="mt-2">
                <SelectWithAdd
                  testId="user-edit-status"
                  value={editForm.status || "Active"}
                  onChange={(v) => setEditForm({ ...editForm, status: v })}
                  options={[{ id: "Active", label: "Active" }, { id: "Inactive", label: "Inactive" }]}
                  disabled={editing?.username === user?.username}
                />
              </div>
              {editing?.username === user?.username && (
                <p className="text-xs vsm-muted mt-1">You can&apos;t deactivate your own account.</p>
              )}
            </div>
            <div className="border-t vsm-border pt-4">
              <Label className="vsm-overline">Profile Photo</Label>
              <div className="mt-2">
                <PhotoPicker
                  testIdPrefix="user-edit-photo"
                  value={editForm.photo}
                  onChange={(v) => setEditForm({ ...editForm, photo: v })}
                />
              </div>
            </div>
            <div className="border-t vsm-border pt-4">
              <Label className="vsm-overline flex items-center gap-2">
                <KeyRound className="w-3 h-3" />
                Reset Password (optional)
              </Label>
              <Input
                data-testid="user-edit-new-password"
                type="password"
                placeholder="Leave blank to keep current password"
                className="rounded-none mt-2"
                value={editForm.new_password}
                onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })}
                minLength={6}
              />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setEditOpen(false)} data-testid="user-edit-cancel">Cancel</Button>
              <Button type="submit" disabled={editing_busy} className="rounded-none vsm-primary-bg hover:opacity-90" data-testid="user-edit-save">
                {editing_busy ? "Saving…" : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
